import { useState, useEffect, useContext } from "react";
import ReactGA from "react-ga4";
import {
  Routes,
  Route,
  HashRouter,
  BrowserRouter,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { SnackbarProvider } from "notistack";
import { SnackbarUtilsConfigurator } from "./utilities/SnackBarUtils";
import { socket } from "./utilities/socket";

import { EventContext } from "./contexts/EventContext";
import Auth from "./pages/Auth/Auth";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Users from "./pages/Users";
import Events from "./pages/Events";
import Calendar from "./pages/Calendar";
import Orders from "./pages/Orders";
import EventController from "./pages/EventController";
import Matchmaking from "./pages/Matchmaking/Matchmaking";
import ProfileQuestions from "./pages/ProfileQuestions";
import Messenger from "./pages/Messenger";
import Admin from "./pages/Admin/Admin";
import Ads from "./pages/Ads/Ads";
import Prompts from "./pages/Prompts/Prompts";
import BlackList from "./pages/BlackList";
import Help from "./pages/Help";
import Reports from "./pages/Reports";
import SystemMessages from "./pages/SystemMessages";
import SystemMessageWatcher from "./components/SystemMessage/SystemMessageWatcher";
import Invoices from "./pages/Invoices";
import InvoiceDetail from "./pages/InvoiceDetail";
import Payouts from "./pages/Payouts";
import Scanner from "./pages/Scanner";
import Waivers from "./pages/Waivers";
import Notifications from "./pages/Notifications";
import Messaging from "./pages/Messaging";
import VoteDisplay from "./pages/EventController/components/VoteDisplay";
import WorkLog from "./pages/WorkLog/WorkLog";
import WorkLogAdmin from "./pages/WorkLog/WorkLogAdmin";

import AuthContext from "./contexts/AuthContext";
import request from "./utilities/requests";
import { toast } from "./utilities/SnackBarUtils";
import eventBus from "./utilities/EventBus";

import "./App.css";

ReactGA.initialize(`${process.env.REACT_APP_GOOGLE_ANALYTICS}`);

const theme = createTheme({
  palette: {
    primary: {
      main: "#ef5241",
    },
  },
  typography: {
    fontFamily: ['"Bitter"', "serif"].join(","),
  },
});

const authTheme = createTheme({
  typography: {
    fontFamily: ['"Instrument Sans"', "sans-serif"].join(","),
  },
});

function AppRoutes({ paymentRequired, paymentOrgId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useContext(EventContext);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(true);
  const workLogEnabled = userData?.org?.config?.workLogEnabled === true;
  const isTimeOnly = workLogEnabled && userData?.permissions?.timeOnly === true;

  useEffect(() => {
    if (isTimeOnly && location.pathname !== "/worklog") {
      navigate("/worklog", { replace: true });
    }
  }, [isTimeOnly, location.pathname, navigate]);

  const isVoteDisplayRoute = location.pathname.startsWith("/votedisplay/");

  if (isVoteDisplayRoute) {
    return (
      <Routes>
        <Route path="/votedisplay/:eventId" element={<VoteDisplay />} />
      </Routes>
    );
  }

  if (paymentRequired && paymentOrgId) {
    return (
      <Layout>
        <Dialog open={paymentDialogOpen}>
          <DialogTitle>Payment Required</DialogTitle>
          <DialogContent>
            <DialogContentText>
              There's an overdue balance on your account. Until it's
              resolved, neither you nor your users will have access to the
              platform. Please review your invoices and submit a payment to get
              everyone back up and running.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPaymentDialogOpen(false)}
              variant="contained"
            >
              View Invoices
            </Button>
          </DialogActions>
        </Dialog>
        <Routes>
          <Route path="/invoices/:orgId" element={<Invoices />} />
          <Route path="/invoice/:id" element={<InvoiceDetail />} />
          <Route
            path="*"
            element={<Navigate to={`/invoices/${paymentOrgId}`} replace />}
          />
        </Routes>
      </Layout>
    );
  }

  if (isTimeOnly) {
    return (
      <Layout>
        <Routes>
          <Route path="/worklog" element={<WorkLog />} />
          <Route path="*" element={<Navigate to="/worklog" replace />} />
        </Routes>
      </Layout>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/users" element={<Users />} />
        <Route path="/events" element={<Events />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/eventcontroller/:id" element={<EventController />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/matchmaking" element={<Matchmaking />} />
        <Route path="/profilequestions" element={<ProfileQuestions />} />
        <Route path="/messenger" element={<Messenger />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/blacklist" element={<BlackList />} />
        <Route path="/ads" element={<Ads />} />
        <Route path="/prompts" element={<Prompts />} />
        <Route path="/help" element={<Help />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/waivers" element={<Waivers />} />
        <Route path="/systemmessages" element={<SystemMessages />} />
        <Route path="/invoices/:orgId" element={<Invoices />} />
        <Route path="/invoice/:id" element={<InvoiceDetail />} />
        <Route path="/payouts/:orgId" element={<Payouts />} />
        <Route path="/scanner" element={<Scanner />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/messaging" element={<Messaging />} />
        <Route
          path="/worklog"
          element={
            !workLogEnabled ? (
              <Navigate to="/" replace />
            ) : userData?.role === "superadmin" ? (
              <Navigate to="/worklog/admin" replace />
            ) : (
              <WorkLog />
            )
          }
        />
        <Route
          path="/worklog/admin"
          element={
            !workLogEnabled ? (
              <Navigate to="/" replace />
            ) : userData?.role === "superadmin" ? (
              <WorkLogAdmin />
            ) : (
              <Navigate to="/worklog" replace />
            )
          }
        />
      </Routes>
    </Layout>
  );
}

function App() {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [tempSession, setTempSession] = useState(null);
  const [signedIn, setSignedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [pairingEvents, setPairingEvents] = useState(null);
  const [pairingEvent, setPairingEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [pagination, setPagination] = useState({});
  const [percentages, setPercentages] = useState({});
  const [roundInfo, setRoundInfo] = useState({});
  const [pairingData, setPairingData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [displayData, setDisplayData] = useState({
    seconds: 0,
    display: "",
    percentage: 0,
    paused: false,
  });
  const [missingRounds, setMissingRounds] = useState([]);
  const [backToBackBreaks, setBackToBackBreaks] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [paymentOrgId, setPaymentOrgId] = useState(null);

  useEffect(() => {
    try {
      const si = localStorage.getItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`,
      );
      if (si === "true") {
        setSignedIn(true);
      }
      if (localStorage.getItem("paymentRequired") === "true") {
        setPaymentRequired(true);
        setPaymentOrgId(localStorage.getItem("paymentOrgId"));
      }
    } catch (e) {
      console.error(`Error with storage: ${e}`);
    } finally {
      setLoading(false);
    }

    const handlePaymentRequired = ({ orgId }) => {
      setPaymentRequired(true);
      setPaymentOrgId(orgId);
    };
    eventBus.on("PAYMENT_REQUIRED", handlePaymentRequired);
    return () => eventBus.off("PAYMENT_REQUIRED", handlePaymentRequired);
  }, []);

  const getUser = async () => {
    try {
      const result = await request(`users`);
      if (!result?.success) {
        if (!result?.paymentRequired) toast.error(result?.error);
        return;
      } else if (!result.user) {
        toast.error("User not found. Please contact support");
        return;
      }

      // Clear payment gate only if the org is now active
      if (result.user?.org?.active !== false) {
        localStorage.removeItem("paymentRequired");
        localStorage.removeItem("paymentOrgId");
        setPaymentRequired(false);
        setPaymentOrgId(null);
      }

      setUserData(result.user);
      if (result.user?.org?.style?.favicon) {
        let fav = document.getElementById("favicon");
        fav.setAttribute("href", result.user.org.style.favicon);
      }
      if (result.user?.org?.style?.title) {
        document.title = result.user.org.style.title;
      }
    } catch (e) {
      const error = `Error retrieving user information. Error: ${e.message}`;
      console.error(error);
      alert(error);
    }
  };

  useEffect(() => {
    if (!signedIn || userData) return;
    getUser();
  }, [signedIn]);

  if (loading) {
    return <div></div>;
  }

  return (
    <AuthContext.Provider
      value={{
        email,
        setEmail,
        signedIn,
        setSignedIn,
        token,
        setToken,
        tempSession,
        setTempSession,
      }}
    >
      <SystemMessageWatcher />

      {signedIn ? (
        <BrowserRouter>
          <SnackbarProvider maxSnack={3}>
            <SnackbarUtilsConfigurator />
            <EventContext.Provider
              value={{
                attendees,
                setAttendees,
                pagination,
                setPagination,
                percentages,
                setPercentages,
                pairingEvents,
                setPairingEvents,
                pairingEvent,
                setPairingEvent,
                roundInfo,
                setRoundInfo,
                displayData,
                setDisplayData,
                pairingData,
                setPairingData,
                groups,
                setGroups,
                missingRounds,
                setMissingRounds,
                backToBackBreaks,
                setBackToBackBreaks,
                userData,
                setUserData,
                socket,
                notificationCount,
                setNotificationCount,
                messageCount,
                setMessageCount,
              }}
            >
              <ThemeProvider theme={theme}>
                <AppRoutes
                  paymentRequired={paymentRequired}
                  paymentOrgId={paymentOrgId}
                />
              </ThemeProvider>
            </EventContext.Provider>
          </SnackbarProvider>
        </BrowserRouter>
      ) : (
        <ThemeProvider theme={authTheme}>
          <Auth />
        </ThemeProvider>
      )}
    </AuthContext.Provider>
  );
}

export default App;
